import React, { useState, useEffect, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Checkbox,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControlLabel,
    Switch,
    Box,
} from "@mui/material";
import { Alert } from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import PaymentIcon from "@mui/icons-material/Payment";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { honorariosConsultaJWT, honorariosGenerarSolicitudJWT } from "../../api/endpoints";
import moment from "moment";


export default function CertificadosTable({ onSolicitudCreated }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState({
        fecha_desde: moment().subtract(1, "month").format("YYYY-MM-DD"),
        fecha_hasta: moment().format("YYYY-MM-DD"),
        buscar: "",
    });
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        count: 0,
    });
    // Ordenamiento por columna. Por defecto ordenado por fecha descendente
    // (más nuevo arriba) — los abogados quieren ver primero lo último cobrado.
    const [orderBy, setOrderBy] = useState('fecha');
    const [order, setOrder] = useState('desc');
    // Por defecto se ocultan los certificados con Costas = 0 (ruido).
    // Toggle para incluirlos cuando el usuario los necesite.
    const [incluirSinCostas, setIncluirSinCostas] = useState(false);
    // Todo lo pendiente sin filtro de fechas: es el número de la card del dashboard.
    // Se muestra al lado del filtrado para que las dos pantallas se lean como una.
    const [totalGeneral, setTotalGeneral] = useState(null);

    const handleSort = (key) => {
        if (orderBy === key) {
            // mismo campo → toggle asc/desc; si ya estaba desc, salir del orden
            if (order === 'asc') setOrder('desc');
            else { setOrderBy(null); setOrder('asc'); }
        } else {
            setOrderBy(key);
            setOrder('asc');
        }
    };

    // Comparador genérico: detecta números y fechas DD/MM/YYYY, fallback string.
    const compararValores = (a, b) => {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        // Número
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        const sa = String(a), sb = String(b);
        // Fecha DD/MM/YYYY
        const reFecha = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const ma = sa.match(reFecha), mb = sb.match(reFecha);
        if (ma && mb) {
            const da = new Date(+ma[3], +ma[2] - 1, +ma[1]);
            const db = new Date(+mb[3], +mb[2] - 1, +mb[1]);
            return da - db;
        }
        return sa.localeCompare(sb, 'es', { numeric: true, sensitivity: 'base' });
    };

    // Lo que efectivamente cobra el abogado por un certificado es Costas + Costas_IVA,
    // ya divididos por Abogados_Participantes en la vista Honorarios_Consulta.
    // Es el mismo cálculo que hace la vista Solicitudes_Plan_Pago_DGR para el campo
    // Honorarios, así que el total de acá coincide con el de la solicitud generada.
    const aCobrar = (row) => Number(row?.Costas || 0) + Number(row?.Costas_IVA || 0);

    const fmtMoneda = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

    // Data ordenada según orderBy/order. Sin orden, retorna data tal cual.
    const dataOrdenada = useMemo(() => {
        if (!orderBy) return data;
        const getCampo = (row) => {
            switch (orderBy) {
                case 'certificado': return `${row.Letra || ''}-${row.Numero || ''}`;
                case 'monto':       return Number(row.Deuda_Certificado || 0);
                case 'costas':      return aCobrar(row);
                case 'fecha':       return row.FPago;
                case 'autos':       return row.Expediente_Autos;
                case 'demandado':   return row.Demandado_NN;
                default:            return row[orderBy];
            }
        };
        const copy = [...data];
        copy.sort((x, y) => {
            const c = compararValores(getCampo(x), getCampo(y));
            return order === 'asc' ? c : -c;
        });
        return copy;
    }, [data, orderBy, order]);

    const totales = useMemo(() => {
        const setSel = new Set(selected);
        let montoTotal = 0, montoSel = 0, ivaTotal = 0, ivaSel = 0;
        for (const row of data) {
            const m = aCobrar(row);
            const iva = Number(row.Costas_IVA || 0);
            montoTotal += m;
            ivaTotal += iva;
            if (setSel.has(row.Id)) { montoSel += m; ivaSel += iva; }
        }
        return { cant: data.length, montoTotal, ivaTotal, cantSel: selected.length, montoSel, ivaSel };
    }, [data, selected]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await honorariosConsultaJWT(filters.fecha_desde, filters.fecha_hasta, filters.buscar, incluirSinCostas);
            if (response.ok) {
                setData(response.data || []);
                setTotalGeneral(response.totalGeneral || null);
            } else {
                showNotification(response.mensaje || "Error al cargar datos", "error");
            }
        } catch (error) {
            // Re-throw SESSION_EXPIRED to trigger the modal
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            showNotification(error?.message || "Error de conexión", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incluirSinCostas]);

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = data.map((n) => n.Id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        setSelected(newSelected);
    };

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const handleSearch = () => {
        fetchData();
    };

    const handleGenerarSolicitud = () => {
        if (selected.length === 0) return;
        setConfirmDialog({ open: true, count: selected.length });
    };

    const handleConfirmGenerar = async () => {
        setConfirmDialog({ open: false, count: 0 });

        setLoading(true);
        try {
            const response = await honorariosGenerarSolicitudJWT(selected);

            if (response.ok) {
                showNotification(response.mensaje, "success");
                setSelected([]);
                fetchData(); // Refresh to remove processed items or update status
                // Notify parent to switch tabs and refresh solicitudes
                if (onSolicitudCreated) {
                    onSolicitudCreated();
                }
            } else {
                showNotification(response.mensaje || "Error al generar solicitud", "error");
            }
        } catch (error) {
            showNotification("Error al procesar la solicitud", "error");
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, severity) => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    return (
        <div>
            <Grid container spacing={2} sx={{ mb: 2, display: "flex", alignItems: "flex-end", gap: 2 }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label="Desde"
                        type="date"
                        value={filters.fecha_desde}
                        onChange={handleFilterChange("fecha_desde")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label="Hasta"
                        type="date"
                        value={filters.fecha_hasta}
                        onChange={handleFilterChange("fecha_hasta")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                        label="Buscar (Autos, Demandado, Nro)"
                        value={filters.buscar}
                        onChange={handleFilterChange("buscar")}
                        fullWidth
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        fullWidth
                    >
                        Buscar
                    </Button>
                </Grid>
            </Grid>
            {/* Toggle para incluir certificados con Costas = 0. Ocultos por
                defecto porque son ruido (no hay nada que cobrar). */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={incluirSinCostas}
                            onChange={(e) => setIncluirSinCostas(e.target.checked)}
                        />
                    }
                    label={<span style={{ fontSize: '0.85rem' }}>Incluir certificados sin costas</span>}
                />
            </Box>
            {/* Resumen de lo que hay en pantalla + lo que se lleva seleccionado, y el
                botón de generar. El abogado necesita ver el total antes de decidir. */}
            <Box
                sx={{
                    mt: 2, mb: 2, p: 1.5,
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
                    border: '1px solid', borderColor: 'divider', borderRadius: '4px',
                    backgroundColor: 'rgba(46,125,50,0.04)',
                }}
            >
                <Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>En este período</Box>
                    <Box sx={{ fontWeight: 600 }}>
                        {totales.cant} · {fmtMoneda(totales.montoTotal)}
                    </Box>
                    {totales.ivaTotal > 0 && (
                        <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                            incluye IVA {fmtMoneda(totales.ivaTotal)}
                        </Box>
                    )}
                </Box>

                {/* El total sin filtrar. Aparece solo cuando el filtro está dejando
                    algo afuera, y es el mismo número de la card "Pendiente de
                    Solicitar" del dashboard: sin esto las dos pantallas mostraban
                    cifras distintas bajo el mismo nombre y parecían contradecirse. */}
                {totalGeneral && totalGeneral.cant > totales.cant && (
                    <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
                        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Pendiente total</Box>
                        <Box sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {totalGeneral.cant} · {fmtMoneda(totalGeneral.monto)}
                        </Box>
                        <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                            ampliá las fechas para verlos
                        </Box>
                    </Box>
                )}

                <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Seleccionados</Box>
                    <Box sx={{ fontWeight: 600, color: totales.cantSel > 0 ? '#2e7d32' : 'text.disabled' }}>
                        {totales.cantSel} · {fmtMoneda(totales.montoSel)}
                    </Box>
                    {totales.ivaSel > 0 && (
                        <Box sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                            incluye IVA {fmtMoneda(totales.ivaSel)}
                        </Box>
                    )}
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PaymentIcon />}
                    onClick={handleGenerarSolicitud}
                    disabled={selected.length === 0 || loading}
                >
                    Generar Solicitud ({selected.length})
                </Button>
            </Box>
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} size="small" aria-label="certificados table">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selected.length > 0 && selected.length < data.length}
                                            checked={data.length > 0 && selected.length === data.length}
                                            onChange={handleSelectAllClick}
                                        />
                                    </TableCell>
                                    {[
                                        { key: 'fecha',       label: 'Fecha',       align: 'left' },
                                        { key: 'certificado', label: 'Certificado', align: 'left' },
                                        { key: 'autos',       label: 'Autos',       align: 'left' },
                                        { key: 'demandado',   label: 'Demandado',   align: 'left' },
                                        { key: 'monto',       label: 'Monto',       align: 'right' },
                                        { key: 'costas',      label: 'A Cobrar',    align: 'right' },
                                    ].map(col => (
                                        <TableCell key={col.key} align={col.align} sortDirection={orderBy === col.key ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === col.key}
                                                direction={orderBy === col.key ? order : 'asc'}
                                                onClick={() => handleSort(col.key)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dataOrdenada.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">No se encontraron certificados.</TableCell>
                                    </TableRow>
                                ) : (
                                    dataOrdenada.map((row, idx) => {
                                        const isItemSelected = isSelected(row.Id);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => handleClick(event, row.Id)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.Id}
                                                selected={isItemSelected}
                                                sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox checked={isItemSelected} />
                                                </TableCell>
                                                <TableCell>{row.FPago}</TableCell>
                                                <TableCell>{`${row.Letra}-${row.Numero}`}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <span>{row.Expediente_Autos}</span>
                                                        {row.Expediente_Id && (
                                                            <Tooltip title="Abrir expediente en nueva ventana" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        window.open(`/expediente/judicial/${row.Expediente_Id}`, '_blank', 'noopener,noreferrer');
                                                                    }}
                                                                    sx={{ p: 0.25 }}
                                                                >
                                                                    <OpenInNewIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{row.Demandado_NN}</TableCell>
                                                <TableCell align="right">{row.Deuda_Certificado?.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</TableCell>
                                                {/* Costas + IVA: es lo que suma la barra de totales y lo que
                                                    termina liquidándose. Mostrar solo Costas hacía que las filas
                                                    no cerraran con el total en los certificados que tienen IVA.
                                                    El desglose aparece únicamente en los que tienen IVA (~4%),
                                                    para no ensuciar el resto con una línea en cero. */}
                                                <TableCell align="right">
                                                    <div>{fmtMoneda(aCobrar(row))}</div>
                                                    {Number(row.Costas_IVA || 0) > 0 && (
                                                        <div style={{ fontSize: '0.7rem', color: '#6d6d6d', whiteSpace: 'nowrap' }}>
                                                            {fmtMoneda(row.Costas)} + IVA {fmtMoneda(row.Costas_IVA)}
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, count: 0 })}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirmar Generación de Solicitud
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        ¿Está seguro de generar una solicitud de pago para <strong>{confirmDialog.count}</strong> certificado{confirmDialog.count !== 1 ? 's' : ''}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, count: 0 })}>
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmGenerar} color="primary" variant="contained" autoFocus>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
